import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Star, Eye } from 'lucide-react';
import { Card, CardContent, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ProductBadges } from './ProductBadges';
import { formatPrice, calculateDiscount } from '../../lib/utils';
import { useCart } from '../../contexts/CartContext';

export const ProductCard = ({ product, viewMode = 'grid', onQuickView }) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const finalPrice = product.discount > 0
    ? calculateDiscount(product.price, product.discount)
    : product.price;

  const handleAddToCart = (e) => {
    e.preventDefault();
    addToCart(product);
  };

  // List view layout
  if (viewMode === 'list') {
    return (
      <Link to={`/product/${product.id}`}>
        <Card className="group overflow-hidden hover:shadow-elegant">
          <div className="flex flex-col sm:flex-row">
            {/* Image */}
            <div className="relative w-full sm:w-48 aspect-square sm:aspect-auto overflow-hidden bg-muted flex-shrink-0">
              {product.images && product.images.length > 0 ? (
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-subtle">
                  <span className="text-4xl text-muted-foreground">NC</span>
                </div>
              )}
              {product.discount > 0 && (
                <Badge variant="destructive" className="absolute top-3 right-3">
                  -{product.discount}%
                </Badge>
              )}
              {product.stock_quantity <= 0 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Badge variant="secondary">Out of Stock</Badge>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 p-4 sm:p-6 flex flex-col">
              <div className="flex-1">
                <p className="text-xs text-muted-foreground mb-1">{product.brand}</p>
                <h3 className="font-serif text-xl font-semibold leading-tight mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-3 mb-4">
                  {product.description}
                </p>

                {/* Rating */}
                {product.rating && (
                  <div className="flex items-center gap-1 mb-4">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="text-sm font-semibold">{product.rating}</span>
                    <span className="text-xs text-muted-foreground">
                      ({product.reviews_count || 0} reviews)
                    </span>
                  </div>
                )}
              </div>

              {/* Price and Action */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-primary">
                    {formatPrice(finalPrice)}
                  </span>
                  {product.discount > 0 && (
                    <span className="text-sm text-muted-foreground line-through">
                      {formatPrice(product.price)}
                    </span>
                  )}
                </div>
                <Button
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity <= 0}
                  className="min-w-[140px]"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  {t('product.addToCart')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </Link>
    );
  }

  // Grid view layout (default)
  return (
    <Link to={`/product/${product.id}`}>
      <Card className="group h-full flex flex-col overflow-hidden hover:shadow-elegant">
        <div className="relative aspect-square overflow-hidden bg-muted">
          {product.images && product.images.length > 0 ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-subtle">
              <span className="text-4xl text-muted-foreground">NC</span>
            </div>
          )}
          
          {/* Product Badges */}
          <ProductBadges product={product} className="absolute top-3 left-3" />
          
          {product.discount > 0 && (
            <Badge variant="destructive" className="absolute top-3 right-3">
              -{product.discount}%
            </Badge>
          )}
          {product.stock_quantity <= 0 && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Badge variant="secondary">Out of Stock</Badge>
            </div>
          )}
          
          {/* Quick View Button */}
          {onQuickView && (
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  onQuickView();
                }}
                className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300"
              >
                <Eye className="w-4 h-4 mr-2" />
                {t('product.quickView')}
              </Button>
            </div>
          )}
        </div>

        <CardContent className="flex-1 p-4 flex flex-col">
          <p className="text-xs text-muted-foreground mb-1">{product.brand}</p>
          <h3 className="font-serif text-lg font-semibold leading-tight mb-2 line-clamp-2">
            {product.name}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
            {product.description}
          </p>

          {/* Rating */}
          {product.rating && (
            <div className="flex items-center gap-1 mb-3">
              <Star className="w-4 h-4 fill-primary text-primary" />
              <span className="text-sm font-semibold">{product.rating}</span>
              <span className="text-xs text-muted-foreground">
                ({product.reviews_count || 0})
              </span>
            </div>
          )}

          <div className="mt-auto">
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-primary">
                {formatPrice(finalPrice)}
              </span>
              {product.discount > 0 && (
                <span className="text-sm text-muted-foreground line-through">
                  {formatPrice(product.price)}
                </span>
              )}
            </div>
          </div>
        </CardContent>

        <CardFooter className="p-4 pt-0">
          <Button
            className="w-full"
            onClick={handleAddToCart}
            disabled={product.stock_quantity <= 0}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            {t('product.addToCart')}
          </Button>
        </CardFooter>
      </Card>
    </Link>
  );
};